/* @flow */

import * as React from 'react';
import * as ramda from 'ramda';
import {RelativeOverlay} from '../relative-overlay';
import {ListViewInfiniteLoading} from './list-view-infinite-loading';

import './list-view-paginated.css';

export type SpecialState = {
    shouldRender: () => boolean,
    component: React.Node,
};

type Props<M> = {|
    collection: M[],
    specialStates: SpecialState[],
    isLoading?: boolean,
    isUpdating?: boolean,
    renderRow: (item: M, index: number) => React.Node,
    getListViewRef?: (?HTMLElement) => void,
    hasNextPage: boolean,
    onFetchMoreRows: () => any,
    itemSpacing?: 8 | 16,
    listPadding?: 8 | 16,
|};

const findSpecialState = ramda.find((state: SpecialState) => {
    return state.shouldRender();
});

const LOAD_MORE_PIXEL_THRESHOLD = 250;

export class ListViewPaginated<M> extends React.Component<Props<M>, void> {
    listRef: ?HTMLElement;

    static defaultProps = {
        collection: [],
        specialStates: [],
    };

    componentDidMount() {
        if (this.listRef) {
            this.listRef.addEventListener('scroll', this.handleScroll);
        }
    }

    componentWillUnmount() {
        if (this.listRef) {
            this.listRef.removeEventListener('scroll', this.handleScroll);
        }
    }

    render() {
        return (
            <React.Fragment>
                <div ref={this.setRefCallback} styleName='list-view-paginated'>
                    {this.renderBody()}
                </div>
                {this.props.isUpdating ? (
                    <RelativeOverlay>
                        <ListViewInfiniteLoading />
                    </RelativeOverlay>
                ) : (
                    undefined
                )}
            </React.Fragment>
        );
    }

    setRefCallback = (listRef: ?HTMLElement) => {
        this.listRef = listRef;

        if (this.props.getListViewRef) {
            this.props.getListViewRef(listRef);
        }
    };

    handleScroll = (e: Event) => {
        // $FlowIgnore these _do_ exist
        const {scrollHeight, offsetHeight, scrollTop} = e.target;

        if (
            scrollHeight - offsetHeight < scrollTop + LOAD_MORE_PIXEL_THRESHOLD &&
            this.props.hasNextPage &&
            !this.props.isLoading
        ) {
            this.props.onFetchMoreRows();
        }
    };

    renderBody = () => {
        const list = (
            <ul styleName={this.props.listPadding ? `pad-${this.props.listPadding}` : undefined}>
                {this.props.collection.map((item, index) => {
                    return (
                        <li
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            styleName={
                                this.props.itemSpacing && index !== this.props.collection.length - 1
                                    ? `item-spacing-${this.props.itemSpacing}`
                                    : undefined
                            }
                        >
                            {this.props.renderRow(item, index)}
                        </li>
                    );
                })}
                {this.props.isLoading || this.props.hasNextPage ? (
                    <ListViewInfiniteLoading />
                ) : (
                    undefined
                )}
            </ul>
        );

        const specialState = findSpecialState(this.props.specialStates);

        return specialState ? specialState.component : list;
    };
}
